import { useCallback, useEffect, useState } from "react";

import api from "../api";
import { CAR_TYPES, DEFAULT_CAR_TYPE, DEFAULT_TONNAGE, FREE_DIRECTION_CAR_TYPE } from "../constants/averageRates";
import {
  areSimilarPointsMatching,
  hasThroughPoints,
  isFreeDirection,
  pickAvailableCarType,
  toPointParam,
} from "../utils/averageRatesHelpers";

const ALL_CAR_TYPE_VALUES = CAR_TYPES.map(item => item.value);

// с лицензией — тент; без неё кузов виден только на бесплатном направлении, там нужен закрытый
const getPreferredCarType = hasLicense => (hasLicense ? DEFAULT_CAR_TYPE : FREE_DIRECTION_CAR_TYPE);

/**
 * Загружает средние ставки по направлению: сначала доступные типы кузова, затем ставки по выбранному.
 * Ставки запрашиваются только при лицензии или на бесплатном направлении.
 * @param {Object} params
 * @param {RouteRequest} params.routeRequest
 * @param {boolean} params.hasLicense
 */
const useAverageRates = ({ routeRequest, hasLicense }) => {
  const fromCityId = routeRequest?.from?.getCityId?.();
  const toCityId = routeRequest?.to?.getCityId?.();

  const [carType, setCarType] = useState(() => getPreferredCarType(hasLicense));
  const [availableCarTypes, setAvailableCarTypes] = useState(null);
  const [rates, setRates] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const canFetchRates = hasLicense || isFreeDirection({ fromCityId, toCityId, carType });

  useEffect(() => {
    setAvailableCarTypes(null);
    setRates(null);
    setIsVisible(false);

    if (!routeRequest || hasThroughPoints(routeRequest)) {
      return undefined;
    }

    const fromPoint = toPointParam(fromCityId);
    const toPoint = toPointParam(toCityId);

    if (!fromPoint || !toPoint) {
      return undefined;
    }

    let cancelled = false;

    api.priceline
      .getSimilarDirections({
        fromPoint,
        toPoint,
        carTypes: ALL_CAR_TYPE_VALUES,
        tonnage: DEFAULT_TONNAGE,
      })
      .then(data => {
        if (cancelled) return;

        if (!areSimilarPointsMatching(fromPoint, toPoint, data) || !data.carTypes?.length) {
          setAvailableCarTypes(null);
          setRates(null);
          setIsVisible(false);
          return;
        }

        const nextCarType = pickAvailableCarType(data.carTypes, getPreferredCarType(hasLicense));

        if (!nextCarType) {
          setAvailableCarTypes(null);
          setRates(null);
          setIsVisible(false);
          return;
        }

        setAvailableCarTypes(data.carTypes);
        setCarType(nextCarType);
        setIsVisible(true);
      })
      .catch(() => {
        if (cancelled) return;
        setAvailableCarTypes(null);
        setRates(null);
        setIsVisible(false);
      });

    return () => {
      cancelled = true;
    };
  }, [routeRequest, fromCityId, toCityId, hasLicense]);

  useEffect(() => {
    if (!availableCarTypes || !carType || !availableCarTypes.includes(carType)) {
      return undefined;
    }

    // без лицензии ставки запрашиваем только на бесплатном направлении
    if (!canFetchRates) {
      setRates(null);
      return undefined;
    }

    const fromPoint = toPointParam(fromCityId);
    const toPoint = toPointParam(toCityId);

    if (!fromPoint || !toPoint) {
      return undefined;
    }

    let cancelled = false;

    api.priceline
      .getLastRates({
        fromPoint,
        toPoint,
        carType,
        tonnage: DEFAULT_TONNAGE,
        nds: true,
      })
      .then(data => {
        if (cancelled) return;
        if (!data || data.averagePrice == null) {
          setRates(null);
          setIsVisible(false);
          return;
        }
        setRates(data);
        setIsVisible(true);
      })
      .catch(() => {
        if (cancelled) return;
        setRates(null);
        setIsVisible(false);
      });

    return () => {
      cancelled = true;
    };
  }, [availableCarTypes, canFetchRates, carType, fromCityId, toCityId]);

  const selectCarType = useCallback(
    value => {
      if (!availableCarTypes?.includes(value)) return;
      setCarType(value);
    },
    [availableCarTypes],
  );

  return {
    carType,
    selectCarType,
    availableCarTypes,
    rates,
    isVisible,
    canFetchRates,
    fromCityId,
    toCityId,
  };
};

export default useAverageRates;
